import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { forkJoin } from 'rxjs';
import { concatMap, map } from 'rxjs/operators';
import { BasePayrollComponent } from '../../../../components/base/base-payroll.component';
import { CompanyDeductionConfig } from '../../../../models/company-deduction-config';
import { CompanyEarningConfig } from '../../../../models/company-earning-config';
import {
  EmployeePay,
  MasterPayrollObject,
  OpenPaylineDetailsArgs,
  Payline,
  PayrollStartData
} from '../../../../models/payroll';
import { CompanyTaxCodeConfig } from '../../../../models/tax-config';
import { CompaniesService } from '../../../../services/companies.service';
import { PayrollService } from '../../../../services/payroll.service';
import { PayrollManagement } from '../../../../tools/payroll-management';
import { IPayrollCorrectionsPage } from '../../../../guards/can-deactivate-correction-payroll.guard';
import { AuditService } from '../../../../services/audit.service';

@Component({
  selector: 'lib-payroll-correction-details',
  templateUrl: './payroll-correction-details.component.html',
  styleUrls: ['./payroll-correction-details.component.css'],
  standalone: false
})
export class PayrollCorrectionDetailsComponent
  extends BasePayrollComponent
  implements OnInit, IPayrollCorrectionsPage
{
  public clientCode: string;
  public year: number;
  public seqNo: number;
  public personId: number;
  public paylineId: number;

  public employeePay: EmployeePay;
  public payline: Payline;
  public paylineDetailArgs: OpenPaylineDetailsArgs;
  public payroll: MasterPayrollObject;

  public deductionConfigs: Record<string, CompanyDeductionConfig> = {};
  public earningConfigs: Record<string, CompanyEarningConfig> = {};
  public taxConfigs: Record<string, CompanyTaxCodeConfig> = {};
  public startData: PayrollStartData;

  public render: boolean;

  get possesiveEmpPayName() {
    const name = this.payrollMgmt.getEmployeeFullName(this.employeePay);
    return name[name.length - 1].toLowerCase() == 's' ? `${name}'` : `${name}'s`;
  }

  constructor(
    private companyService: CompaniesService,
    public payrollMgmt: PayrollManagement,
    private payrollService: PayrollService,
    private auditService: AuditService,
    private router: Router
  ) {
    super();
  }

  ngOnInit(): void {
    const p = this.route.snapshot.paramMap;
    this.clientCode = p.get('client_code');
    this.year = +p.get('year');
    this.seqNo = +p.get('seqNo');
    this.personId = +p.get('personId');
    this.paylineId = +p.get('paylineId');

    this.paylineDetailArgs = new OpenPaylineDetailsArgs(
      null,
      this.paylineId,
      this.personId,
      null,
      null,
      false,
      true,
      false
    );

    forkJoin([
      this.companyService.getTaxConfigs(this.clientCode, true).pipe(
        map(data => {
          data.forEach(config => {
            this.taxConfigs[config.TaxCode] = config;
          });
        })
      ),
      this.payrollService
        .getPayrollStartData(this.clientCode)
        .pipe(map(data => (this.startData = data))),
      this.payrollMgmt.getPayroll(this.clientCode, this.year, this.seqNo).pipe(
        concatMap(payroll => {
          this.payroll = payroll;

          return this.payrollService
            .getPayrollConfigSetupData(
              this.clientCode,
              [],
              payroll.LocationId,
              payroll.PayDate,
              payroll.PayType
            )
            .pipe(
              map(setupData => {
                setupData.Deductions.forEach(config => {
                  this.deductionConfigs[config.Id] = config;
                });

                setupData.Earnings.forEach(config => {
                  this.earningConfigs[config.Id] = config;
                });

                const employeePay = payroll.EmployeePays.find(x => x.PersonId == this.personId);
                this.employeePay = employeePay;
                this.payline = employeePay.Paylines.find(x => x.PaylineId == this.paylineId);
                return employeePay;
              })
            );
        }),
        map(employeePay => {
          return this.payrollService.calculatePaylines(
            this.clientCode,
            this.personId,
            this.year,
            this.seqNo,
            employeePay.Paylines,
            false,
            false
          );
        })
      )
    ]).subscribe(() => {
      this.render = true;
    });
  }

  public back() {
    this.payline.OkToPay = false;
    this.payrollService
      .calculatePaylines(
        this.clientCode,
        this.personId,
        this.year,
        this.seqNo,
        this.employeePay.Paylines
      )
      .subscribe(paylines => {
        this.employeePay.Paylines = paylines;
        this.payrollMgmt.updateEmployeePay(
          this.clientCode,
          this.year,
          this.seqNo,
          this.employeePay
        );

        this.router.navigate([`/client/${this.clientCode}/payroll-corrections`]);
      });
  }

  public continue() {
    this.router.navigate([`/client/${this.clientCode}/payroll-corrections`]);
  }
}
